/**
 * GoalFormPage
 * Page wrapper for goal creation/editing
 * BP-06-03: Care Team & Goals Frontend
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { GoalForm } from '../components';
import { useCreateGoal, useUpdateGoal } from '@/features/goals/hooks/useGoals';
import type { CreateGoalRequest } from '@/features/goals/types/goalsTypes';

export function GoalFormPage() {
  const { childId, goalId } = useParams<{ childId: string; goalId?: string }>();
  const navigate = useNavigate();
  const isEditing = !!goalId;

  const createMutation = useCreateGoal();
  const updateMutation = useUpdateGoal();

  const handleSubmit = async (data: CreateGoalRequest) => {
    if (isEditing) {
      await updateMutation.mutateAsync({ goalId: goalId!, data });
      navigate(`/children/${childId}/goals/${goalId}`);
    } else {
      const newGoal = await createMutation.mutateAsync({ childId: childId!, data });
      navigate(`/children/${childId}/goals/${newGoal.id}`);
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      navigate(`/children/${childId}/goals/${goalId}`);
    } else {
      navigate(`/children/${childId}/goals`);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={handleCancel}
        className="inline-flex items-center gap-2 text-warm-gray hover:text-navy mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to {isEditing ? 'Goal' : 'Goals'}
      </button>

      {/* Form Card */}
      <div className="bg-white rounded-2xl border border-warm-gray/10 p-6 sm:p-8">
        <GoalForm
          childId={childId!}
          goalId={goalId}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      </div>
    </div>
  );
}

export default GoalFormPage;


